import React, { useState, useCallback } from 'react';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import useEmblaCarousel from 'embla-carousel-react';
import Menu from './Menu';
import { facilities } from '@/lib/config';

const RoomCarousel = ({ images, price }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  return (
    <div className="room-carousel">
      <div className="embla" ref={emblaRef}>
        <div className="embla__container">
          {images.map((image, index) => (
            <div className="embla__slide" key={index}>
              <img src={image} alt={`View ${index + 1}`} className="room-carousel-image" />
            </div>
          ))}
        </div>
      </div>
      <button className="embla__prev" onClick={scrollPrev}>
        <ChevronLeft size={24} />
      </button>
      <button className="embla__next" onClick={scrollNext}>
        <ChevronRight size={24} />
      </button>
      {price && <div className="room-price-badge">₹{price.toLocaleString()}/night</div>}
    </div>
  );
};

const Facilities = () => {
  const [selectedFacility, setSelectedFacility] = useState(facilities[0]);

  return (
    <section id="facilities" className="facilities-section">
      <div className="section-container">
        <div className="section-header">
          <h2 className="section-title">Luxury-Class Facilities</h2>
          <p className="section-subtitle">
            Experience unparalleled comfort and luxury at every turn
          </p>
        </div>

        <div className="facilities-navigation">
          {facilities.map((facility) => (
            <button
              key={facility.id}
              onClick={() => setSelectedFacility(facility)}
              className={`facility-nav-button ${
                selectedFacility.id === facility.id ? 'active' : ''
              }`}
            >
              {facility.name}
            </button>
          ))}
        </div>

        {selectedFacility.isRoomCategory ? (
          <div className="rooms-showcase">
            {selectedFacility.roomTypes.map((room, index) => (
              <div key={room.id} className={`room-card ${index === 0 ? 'room-left' : 'room-right'}`}>
                <RoomCarousel images={room.images} price={room.price} />
                
                <div className="room-details">
                  <h3 className="room-title">{room.name}</h3>
                  <p className="room-description">{room.description}</p>
                  
                  <div className="room-features">
                    <h4 className="features-heading">Features & Amenities</h4>
                    <ul className="features-list">
                      {room.features.map((feature, idx) => (
                        <li key={idx} className="feature-item">
                          <Check size={18} className="feature-check" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="room-actions-group">
                    <Button
                      onClick={() => {
                        const roomSlug = room.id === 1 ? 'luxury' : 'premium';
                        window.location.href = `/room/${roomSlug}`;
                      }}
                      variant="outline"
                      className="room-details-btn"
                    >
                      View Details
                    </Button>
                    <Button
                      onClick={() => document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' })}
                      className="room-cta"
                    >
                      Book {room.name}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : selectedFacility.isRestaurantCategory ? (
          <div className="restaurants-showcase">
            {selectedFacility.restaurantTypes.map((restaurant, index) => (
              <div key={restaurant.id} className={`restaurant-card ${index === 0 ? 'restaurant-left' : 'restaurant-right'}`}>
                <div className="restaurant-carousel">
                  <RoomCarousel images={restaurant.images} />
                </div>
                
                <div className="restaurant-details">
                  <h3 className="restaurant-title">{restaurant.name}</h3>
                  <p className="restaurant-description">{restaurant.description}</p>
                  
                  <div className="restaurant-features">
                    <h4 className="features-heading">Features & Amenities</h4>
                    <ul className="features-list">
                      {restaurant.features.map((feature, idx) => (
                        <li key={idx} className="feature-item">
                          <Check size={18} className="feature-check" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button
                    onClick={() => document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' })}
                    className="restaurant-cta"
                  >
                    Reserve Table
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="facility-showcase">
            <div className="facility-image-container">
              <img
                src={selectedFacility.image}
                alt={selectedFacility.name}
                className="facility-image"
              />
            </div>
            
            <div className="facility-details">
              <h3 className="facility-title">{selectedFacility.name}</h3>
              <p className="facility-description">{selectedFacility.description}</p>
              
              <div className="facility-features">
                <h4 className="features-heading">Features & Amenities</h4>
                <ul className="features-list">
                  {selectedFacility.features.map((feature, index) => (
                    <li key={index} className="feature-item">
                      <Check size={20} className="feature-check" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Button
                onClick={() => document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' })}
                className="facility-cta"
              >
                Book Now
              </Button>
            </div>
          </div>
        )}

        {/* Display Menu if facility has menu */}
        {/* {(selectedFacility.isRestaurantCategory || (!selectedFacility.isRoomCategory && selectedFacility.hasMenu)) && selectedFacility.menu && (
          <Menu menu={selectedFacility.menu} />
        )} */}
      </div>
    </section>
  );
};

export default Facilities;
